/**
 * Hook de estado de filtros - V05.1
 * PTEL - Sistema de Normalización de Coordenadas
 */

import { useState, useCallback, useEffect } from 'react';
import type { InfrastructureType } from '../../../types/infrastructure';
import type { MarkerGroup } from '../../../constants/markerConfig';
import { MARKER_CONFIGS } from '../../../constants/markerConfig';
import type { FilterState } from './types';
import { DEFAULT_FILTER_STATE } from './types';
import { getTypesInGroup } from './filterUtils';

export interface UseFilterStateOptions {
  initialState?: Partial<FilterState>;
  persist?: boolean;
  storageKey?: string;
}

export interface UseFilterStateReturn {
  filters: FilterState;
  setFilters: (filters: FilterState) => void;
  toggleType: (type: InfrastructureType) => void;
  toggleGroup: (group: MarkerGroup) => void;
  toggleGroupExpanded: (group: MarkerGroup) => void;
  setScoreRange: (min: number, max: number) => void;
  setSearchText: (text: string) => void;
  resetFilters: () => void;
  selectAllTypes: () => void;
  deselectAllTypes: () => void;
}

const STORAGE_KEY_DEFAULT = 'ptel-filter-state';

export function useFilterState(options: UseFilterStateOptions = {}): UseFilterStateReturn {
  const {
    initialState = {},
    persist = false,
    storageKey = STORAGE_KEY_DEFAULT,
  } = options;

  // Cargar estado inicial desde storage si persist=true
  const getInitialState = (): FilterState => {
    if (persist && typeof window !== 'undefined') {
      try {
        const stored = sessionStorage.getItem(storageKey);
        if (stored) {
          return { ...DEFAULT_FILTER_STATE, ...JSON.parse(stored) };
        }
      } catch { /* ignore */ }
    }
    return { ...DEFAULT_FILTER_STATE, ...initialState };
  };

  const [filters, setFiltersInternal] = useState<FilterState>(getInitialState);

  // Persistir cambios
  useEffect(() => {
    if (persist && typeof window !== 'undefined') {
      sessionStorage.setItem(storageKey, JSON.stringify(filters));
    }
  }, [filters, persist, storageKey]);

  const setFilters = useCallback((newFilters: FilterState) => {
    setFiltersInternal(newFilters);
  }, []);

  const toggleType = useCallback((type: InfrastructureType) => {
    setFiltersInternal(prev => {
      const exists = prev.selectedTypes.includes(type);
      return {
        ...prev,
        selectedTypes: exists
          ? prev.selectedTypes.filter(t => t !== type)
          : [...prev.selectedTypes, type],
      };
    });
  }, []);

  const toggleGroup = useCallback((group: MarkerGroup) => {
    const typesInGroup = getTypesInGroup(group);
    setFiltersInternal(prev => {
      const allSelected = typesInGroup.every(t => prev.selectedTypes.includes(t));
      if (allSelected) {
        // Deseleccionar todos del grupo
        return {
          ...prev,
          selectedTypes: prev.selectedTypes.filter(t => !typesInGroup.includes(t)),
        };
      } else {
        // Seleccionar todos del grupo
        const newTypes = new Set([...prev.selectedTypes, ...typesInGroup]);
        return { ...prev, selectedTypes: Array.from(newTypes) };
      }
    });
  }, []);

  const toggleGroupExpanded = useCallback((group: MarkerGroup) => {
    setFiltersInternal(prev => ({
      ...prev,
      expandedGroups: prev.expandedGroups.includes(group)
        ? prev.expandedGroups.filter(g => g !== group)
        : [...prev.expandedGroups, group],
    }));
  }, []);

  const setScoreRange = useCallback((min: number, max: number) => {
    setFiltersInternal(prev => ({ ...prev, minScore: min, maxScore: max }));
  }, []);

  const setSearchText = useCallback((text: string) => {
    setFiltersInternal(prev => ({ ...prev, searchText: text }));
  }, []);

  const resetFilters = useCallback(() => {
    setFiltersInternal(DEFAULT_FILTER_STATE);
  }, []);

  const selectAllTypes = useCallback(() => {
    const allTypes = Object.values(MARKER_CONFIGS).map(c => c.type);
    setFiltersInternal(prev => ({ ...prev, selectedTypes: allTypes }));
  }, []);

  const deselectAllTypes = useCallback(() => {
    setFiltersInternal(prev => ({ ...prev, selectedTypes: [] }));
  }, []);

  return {
    filters,
    setFilters,
    toggleType,
    toggleGroup,
    toggleGroupExpanded,
    setScoreRange,
    setSearchText,
    resetFilters,
    selectAllTypes,
    deselectAllTypes,
  };
}
